import { useContext, useMemo } from 'react';

import BoardContext from 'contexts/BoardContext';
import FilterPlatesContext from 'contexts/FilterPlatesContext';
import useFilterInput from 'hooks/useFilterInput';
import useQuickFilterCounts from 'hooks/useQuickFilterCounts';
import useToFixedFilters from 'hooks/useToFixedFilters';
import toFilterInput from 'toFilterInput';
import { DefaultDatasetFiltersContext } from '../../../contextProviders/SplashScreenProviders/DatasetProviders/DefaultDatasetFiltersProvider';

/**
 * Record counts for a board's quick filters, using the same filters as the
 * board's grid (see components/Grid and PerformanceBoard/Grid)
 */
const useBoardQuickFilterCounts = ({
  isEnabled,
}: {
  isEnabled: boolean;
}): QuickFilterCounts.CountsById => {
  const { board, quickFilters, selectedQuickFilters, fixedQuickFilters } =
    useContext(BoardContext);
  const { isDefaultFiltersDisabled } = useContext(FilterPlatesContext);
  const { defaultDatasetFiltersLookup } = useContext(
    DefaultDatasetFiltersContext,
  );
  const toDrillDowns = useToFixedFilters();
  const { filterInput } = useFilterInput({ ignoreQuickFilters: true });

  const baseFilters = useMemo((): FilterInput[] => {
    const result: FilterInput[] = [filterInput, ...(fixedQuickFilters || [])];
    if (!isDefaultFiltersDisabled) {
      const datasetFilter = defaultDatasetFiltersLookup[board.dataType];
      if (datasetFilter) {
        result.push(datasetFilter.filterInput);
      }
    }

    return result.filter((f) => Object.keys(f).length > 0);
  }, [
    board.dataType,
    defaultDatasetFiltersLookup,
    filterInput,
    fixedQuickFilters,
    isDefaultFiltersDisabled,
  ]);

  const selectedFilters = useMemo(
    () =>
      selectedQuickFilters.map((qf) =>
        toFilterInput(qf, window.emptyArray, toDrillDowns),
      ),
    [selectedQuickFilters, toDrillDowns],
  );

  const candidates = useMemo(
    (): QuickFilterCounts.Candidate[] =>
      quickFilters.map((qf) => ({
        id: qf.id,
        filterInput: toFilterInput(qf, window.emptyArray, toDrillDowns),
        isSelected: selectedQuickFilters.some((sf) => sf.id === qf.id),
      })),
    [quickFilters, selectedQuickFilters, toDrillDowns],
  );

  return useQuickFilterCounts({
    dataType: board.dataType,
    baseFilters,
    selectedFilters,
    candidates,
    isEnabled,
  });
};

export default useBoardQuickFilterCounts;
